import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  bio?: string;
  location?: {
    type: 'Point';
    coordinates: [number, number];
    name: string;
  };
  interests: string[];
  verification: {
    status: 'bronze' | 'silver' | 'platinum' | 'gold' | 'diamond';
    votes: number;
    verifiedAt?: Date;
  };
  reputationScore: number;
  followers: string[];
  following: string[];
  preferences: {
    gender: 'male' | 'female';
    notifications: {
      messages: boolean;
      likes: boolean;
      comments: boolean;
      challenges: boolean;
    };
    privacy: {
      profileVisibility: 'public' | 'private';
      locationSharing: boolean;
    };
  };
  createdAt: Date;
  updatedAt: Date;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  updatePreferences: (preferences: Partial<User['preferences']>) => void;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  gender: 'male' | 'female';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const checkAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData.isAuthenticated) {
            // In real app, validate token with server
            setUser(userData);
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock user data - in real app, this would come from server
      const mockUser: User = {
        id: '1',
        username: 'demo_user',
        email: email,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        bio: 'Welcome to DuoWeave!',
        location: {
          type: 'Point',
          coordinates: [-74.006, 40.7128],
          name: 'New York, NY'
        },
        interests: ['technology', 'music', 'travel'],
        verification: {
          status: 'bronze',
          votes: 0
        },
        reputationScore: 150,
        followers: [],
        following: [],
        preferences: {
          gender: 'male',
          notifications: {
            messages: true,
            likes: true,
            comments: true,
            challenges: true
          },
          privacy: {
            profileVisibility: 'public',
            locationSharing: true
          }
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setUser(mockUser);
      localStorage.setItem('user', JSON.stringify({
        ...mockUser,
        isAuthenticated: true,
        loginTime: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock user data - in real app, this would come from server
      const mockUser: User = {
        id: '1',
        username: userData.username,
        email: userData.email,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        bio: 'Welcome to DuoWeave!',
        location: {
          type: 'Point',
          coordinates: [-74.006, 40.7128],
          name: 'New York, NY'
        },
        interests: [],
        verification: {
          status: 'bronze',
          votes: 0
        },
        reputationScore: 0,
        followers: [],
        following: [],
        preferences: {
          gender: userData.gender,
          notifications: {
            messages: true,
            likes: true,
            comments: true,
            challenges: true
          },
          privacy: {
            profileVisibility: 'public',
            locationSharing: true
          }
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setUser(mockUser);
      localStorage.setItem('user', JSON.stringify({
        ...mockUser,
        isAuthenticated: true,
        registrationTime: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData, updatedAt: new Date() };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify({
        ...updatedUser,
        isAuthenticated: true
      }));
    }
  };

  const updatePreferences = (preferences: Partial<User['preferences']>) => {
    if (user) {
      const updatedUser = {
        ...user,
        preferences: { ...user.preferences, ...preferences },
        updatedAt: new Date()
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify({
        ...updatedUser,
        isAuthenticated: true
      }));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    updatePreferences
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
